import type { IntlShape } from "react-intl";
import { defineMessages } from "react-intl";

// Shared by every Plaid connect toast, so a new one replaces the previous instead of stacking.
export const PLAID_CONNECT_TOAST_ID = "plaid-connect";

const FALLBACK_INSTITUTION_NAME_MESSAGE = defineMessages({
  fallbackInstitutionName: {
    id: "bankconnect: plaid error toast fallback institution name",
    defaultMessage: "Bank",
  },
});

const PLAID_ERROR_TOAST_MESSAGES = defineMessages({
  requires_credentials: {
    id: "bankconnect: plaid error toast requires_credentials",
    defaultMessage: "You didn't finish signing into {institutionName}. Try again.",
  },
  requires_oauth: {
    id: "bankconnect: plaid error toast requires_oauth",
    defaultMessage: "You didn't finish signing into {institutionName}. Try again.",
  },
  requires_code: {
    id: "bankconnect: plaid error toast requires_code",
    defaultMessage: "You didn't enter your {institutionName} security code. Try again.",
  },
  requires_questions: {
    id: "bankconnect: plaid error toast requires_questions",
    defaultMessage: "You didn't answer your {institutionName} security questions. Try again.",
  },
  choose_device: {
    id: "bankconnect: plaid error toast choose_device",
    defaultMessage: "You didn't choose where to get your {institutionName} security code. Try again.",
  },
  requires_account_selection: {
    id: "bankconnect: plaid error toast requires_account_selection",
    defaultMessage: "You didn't choose which {institutionName} account to connect. Try again.",
  },
  requires_recaptcha: {
    id: "bankconnect: plaid error toast requires_recaptcha",
    defaultMessage: "You didn't finish the {institutionName} security check. Try again.",
  },
  user_input_timeout: {
    id: "bankconnect: plaid error toast user_input_timeout",
    defaultMessage: "Your {institutionName} connection timed out. Try again.",
  },
  oauth_state_id_already_processed: {
    id: "bankconnect: plaid error toast oauth_state_id_already_processed",
    defaultMessage: "{institutionName} may already be connected. Check your accounts above before trying again.",
  },
  oauth_state_id_not_found: {
    id: "bankconnect: plaid error toast oauth_state_id_not_found",
    defaultMessage: "Your {institutionName} connection was interrupted. Try again.",
  },
  incorrect_link_token: {
    id: "bankconnect: plaid error toast incorrect_link_token",
    defaultMessage: "Your {institutionName} connection was interrupted. Try again.",
  },
  incorrect_oauth_nonce: {
    id: "bankconnect: plaid error toast incorrect_oauth_nonce",
    defaultMessage: "Your {institutionName} connection was interrupted. Try again.",
  },
  institution_not_found: {
    id: "bankconnect: plaid error toast institution_not_found",
    defaultMessage: "We couldn't find that bank. Check the name and search again.",
  },
  invalid_credentials: {
    id: "bankconnect: plaid error toast invalid_credentials",
    defaultMessage: "Your {institutionName} login didn't work. Try again.",
  },
  insufficient_credentials: {
    id: "bankconnect: plaid error toast insufficient_credentials",
    defaultMessage: "Your {institutionName} login didn't work. Try again.",
  },
  invalid_mfa: {
    id: "bankconnect: plaid error toast invalid_mfa",
    defaultMessage: "{institutionName} didn't accept your security details. Try again.",
  },
  institution_not_responding: {
    id: "bankconnect: plaid error toast institution_not_responding",
    defaultMessage:
      "{institutionName} is temporarily unavailable. Try again shortly and if the issue persists, upload statements to continue.",
  },
  internal_server_error: {
    id: "bankconnect: plaid error toast internal_server_error",
    defaultMessage:
      "{institutionName} is temporarily unavailable. Try again shortly and if the issue persists, upload statements to continue.",
  },
  institution_down: {
    id: "bankconnect: plaid error toast institution_down",
    defaultMessage:
      "{institutionName} is temporarily unavailable. Try again shortly and if the issue persists, upload statements to continue.",
  },
  institution_not_available: {
    id: "bankconnect: plaid error toast institution_not_available",
    defaultMessage:
      "{institutionName} is temporarily unavailable. Try again shortly and if the issue persists, upload statements to continue.",
  },
  item_locked: {
    id: "bankconnect: plaid error toast item_locked",
    defaultMessage:
      "{institutionName} locked your account after too many attempts. Unlock it with {institutionName}, or upload statements to continue.",
  },
  user_setup_required: {
    id: "bankconnect: plaid error toast user_setup_required",
    defaultMessage:
      "{institutionName} needs you to finish setup on their site first. Complete this and then reconnect, or upload statements to continue.",
  },
  item_not_supported: {
    id: "bankconnect: plaid error toast item_not_supported",
    defaultMessage: "This type of {institutionName} account can't be connected live. Upload statements to continue.",
  },
  mfa_not_supported: {
    id: "bankconnect: plaid error toast mfa_not_supported",
    defaultMessage:
      "{institutionName}'s security setup isn't supported for a live connection. Upload statements to continue.",
  },
  no_accounts: {
    id: "bankconnect: plaid error toast no_accounts",
    defaultMessage: "{institutionName} didn't return any accounts we can connect. Upload statements to continue.",
  },
  institution_no_longer_supported: {
    id: "bankconnect: plaid error toast institution_no_longer_supported",
    defaultMessage: "{institutionName} is no longer available for a live connection. Upload statements to continue.",
  },
  institution_not_supported: {
    id: "bankconnect: plaid error toast institution_not_supported",
    defaultMessage: "{institutionName} isn't supported for a live connection. Upload statements to continue.",
  },
  fallback: {
    id: "bankconnect: plaid error toast fallback",
    defaultMessage: "Something went wrong connecting your bank. Try again.",
  },
});

type PlaidErrorToastCode = Exclude<keyof typeof PLAID_ERROR_TOAST_MESSAGES, "fallback">;

const isKnownCode = (code: string): code is PlaidErrorToastCode =>
  Object.prototype.hasOwnProperty.call(PLAID_ERROR_TOAST_MESSAGES, code);

export const getPlaidErrorToastMessage = (
  intl: IntlShape,
  code: string | null,
  institutionName: string | null
): string => {
  const normalizedCode = code?.toLowerCase() ?? null;
  const descriptor =
    normalizedCode && isKnownCode(normalizedCode)
      ? PLAID_ERROR_TOAST_MESSAGES[normalizedCode]
      : PLAID_ERROR_TOAST_MESSAGES.fallback;
  const resolvedInstitutionName =
    institutionName || intl.formatMessage(FALLBACK_INSTITUTION_NAME_MESSAGE.fallbackInstitutionName);
  return intl.formatMessage(descriptor, { institutionName: resolvedInstitutionName });
};
